import { useEffect, useRef, useState } from "react";

const useInterval = (props = {}) => {
  const { delay, intervalBeforeExpire = 0, onExpire, action = null, executeOnInit = false } = props;

  if (!delay) {
    throw new Error("useInterval hook requires 'delay' props!");
  }

  const intervalIdRef = useRef(null);
  const [intervalId, setIntervalId] = useState(intervalIdRef.current);

  const executedIntervalsRef = useRef(0);
  const [executedIntervals, setExecutedIntervals] = useState(executedIntervalsRef.current);

  useEffect(() => {
    intervalIdRef.current = intervalId;
    executedIntervalsRef.current = executedIntervals;
  });

  const executeAction = (isExecuteOnInit = false) => {
    const nextExecutedInterval = executedIntervalsRef.current + 1;
    setExecutedIntervals(nextExecutedInterval);
    if (intervalBeforeExpire && nextExecutedInterval > intervalBeforeExpire) {
      // expire, do not execute
    } else {
      // provide hook API as props into action
      action?.({ stopTimer, isTimerRunning, isExecuteOnInit });
    }
  };

  let blocked = false;
  const startTimer = (interval = delay) => {
    if (!blocked) {
      blocked = true;
      // reset executed counters
      setExecutedIntervals(0);
      executedIntervalsRef.current = 0;

      if (executeOnInit) {
        console.debug("[useInterval] executeOnInit tick");
        executeAction(executeOnInit);
      }

      if (!interval) {
        console.warn("[useInterval] startTimer receives invalid 'interval' prop!");
      }

      console.debug("[useInterval] startTimer");
      setIntervalId(
        setInterval(
          () => {
            console.debug("[useInterval] tick");
            executeAction();
          },
          (interval || delay) * 1000,
        ),
      );
    } else {
      console.warn("[useInterval] ongoing blocking interval");
    }
  };

  const stopTimer = () => {
    if (intervalIdRef.current) {
      console.debug("[useInterval] stopTimer", intervalIdRef.current);
      clearInterval(intervalIdRef.current);
      blocked = false;
      setIntervalId(null);
    }
  };

  const isTimerRunning = () => {
    return !!intervalId;
  };

  useEffect(() => {
    console.debug("[useInterval] execution incremented ", {
      executedIntervals,
      intervalBeforeExpire,
    });
    if (intervalBeforeExpire && executedIntervals > intervalBeforeExpire) {
      console.warn(`[useInterval] timer expired after ${executedIntervals} executions.`);
      onExpire?.({ stopTimer, isTimerRunning });
      stopTimer();
    }
  }, [executedIntervals]);

  useEffect(() => {
    return () => {
      stopTimer();
    };
  }, []);

  return { startTimer, stopTimer, isTimerRunning };
};

export default useInterval;
